/**
 * CheckEmailScreen.tsx — Écran plein écran affiché après une inscription réussie.
 *
 * Remplace le simple toast « Vérifiez votre email » : un écran immersif et
 * animé qui confirme l'envoi du lien de confirmation, rappelle de vérifier les
 * spams, et permet de renvoyer l'email (resend Supabase, avec cooldown).
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { S1Logo } from "@/components/S1Logo";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const RESEND_COOLDOWN_SECONDS = 30;

interface CheckEmailScreenProps {
  /** Email auquel le lien de confirmation a été envoyé. */
  email: string;
  /** Retour à l'écran de connexion. */
  onBack: () => void;
}

export function CheckEmailScreen({ email, onBack }: CheckEmailScreenProps) {
  const { toast } = useToast();
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Décrément du cooldown anti-spam (1 tick / seconde).
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (sending || cooldown > 0) return;
    setSending(true);
    try {
      const { error } = await supabase.auth.resend({ type: "signup", email });
      if (error) throw error;
      toast({
        title: "Email renvoyé",
        description: "Regarde ta boîte mail (et tes spams).",
      });
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      toast({
        title: "Envoi impossible",
        description: err instanceof Error ? err.message : "Réessaie dans un instant.",
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-background px-4 py-10">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-sm text-center"
      >
        <div className="flex justify-center mb-8">
          <S1Logo variant="full" height={48} />
        </div>

        {/* Icône enveloppe + halo pulsant */}
        <div className="flex justify-center mb-6">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 18, delay: 0.1 }}
            className="relative flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 border border-primary/20"
          >
            <motion.span
              className="absolute inset-0 rounded-full bg-primary/20"
              animate={{ scale: [1, 1.45, 1], opacity: [0.5, 0, 0.5] }}
              transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
            />
            <Mail className="relative h-9 w-9 text-primary" />
          </motion.div>
        </div>

        <motion.h1
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.2, ease: "easeOut" }}
          className="text-2xl font-bold tracking-tight"
        >
          Vérifie ta boîte mail
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.3, ease: "easeOut" }}
          className="mt-3 space-y-3"
        >
          <p className="text-sm text-muted-foreground">
            On vient d'envoyer un lien de confirmation à{" "}
            <span className="font-semibold text-foreground break-all">{email}</span>.
            Clique dessus pour activer ton compte.
          </p>
          <p className="text-xs text-muted-foreground">
            Tu ne le vois pas ? Pense à regarder tes courriers indésirables (spams).
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.4, ease: "easeOut" }}
          className="mt-8 space-y-3"
        >
          <Button
            onClick={handleResend}
            disabled={sending || cooldown > 0}
            variant="outline"
            className="w-full gap-2"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Envoi…
              </>
            ) : cooldown > 0 ? (
              `Renvoyer l'email (${cooldown}s)`
            ) : (
              "Renvoyer l'email"
            )}
          </Button>

          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            Revenir à la connexion
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
}
